// @ts-check
import React from "react";
import { useIntl } from "react-intl";
import { Box, VStack, List, StackDivider, Heading, Flex, useTheme } from "@chakra-ui/react";

import SectionHeader from "./SectionHeader";
import FormattedTransactionItem, {
  TransactionItemSkeleton,
  TransactionItem,
} from "./TransactionItem";
import { groupTransactionsByDay } from "../utils";

import BidirectionalMoney from "components/Icons/BidirectionalMoney";

/**
 * TransactionList
 * @description
 * Presentational component for displaying a single section of a list
 * of transactions.
 * @param {object} props
 * @param {string} props.sectionTitle Title of the section
 * @param {Transaction[]} props.transactions An array of transactions
 * @param {string} props.accountId Account ID of the current user
 * @returns JSX.Element
 */
export const TransactionList = ({ sectionTitle, transactions, accountId }) => {
  return (
    <VStack spacing={3} align="stretch" px={4}>
      <SectionHeader title={sectionTitle} numberOfTransactions={transactions.length} />
      <VStack spacing={2} align="stretch" divider={<StackDivider />} as={List}>
        {transactions.map((transaction, index) => (
          <FormattedTransactionItem
            transaction={transaction}
            accountId={accountId}
            key={`transaction-item-${index}`}
          />
        ))}
      </VStack>
    </VStack>
  );
};

/**
 * TransactionListGroupedByDay
 * @description
 * Presentational component for displaying transaction history and grouping items
 * by day.
 * @param {object} props
 * @param {Transaction[]} props.transactions An array of transactions
 * @param {string} props.accountId account ID of the current user
 * @returns JSX.Element
 */
export const TransactionListGroupedByDay = ({ transactions, accountId }) => {
  const { formatDate } = useIntl();
  const transactionsGroupedByDay = groupTransactionsByDay(transactions, formatDate);

  return (
    <VStack spacing={5} align="stretch" divider={<StackDivider />}>
      {Object.entries(transactionsGroupedByDay).map(([day, transactions], index) => (
        <TransactionList
          sectionTitle={day}
          transactions={transactions}
          accountId={accountId}
          key={`transaction-list-${index}`}
        />
      ))}
    </VStack>
  );
};

/**
 * TransactionListSkeletons
 * @description
 * Skeleton component for transaction list.
 * @returns JSX.Element
 */
export const TransactionListSkeletons = () => {
  return (
    <VStack spacing={3} align="stretch" px={4} divider={<StackDivider />}>
      <TransactionItemSkeleton />
      <TransactionItemSkeleton />
      <TransactionItemSkeleton />
    </VStack>
  );
};

/**
 * NoTransactions
 * @description
 * The empty state component for transaction list.
 * @param {object} props
 * @param {string} props.description Description to show above the blurred out list of transactions
 * @returns
 */
export const NoTransactions = ({ description }) => {
  return (
    <Box flex={1} position="relative">
      <Box
        position="absolute"
        top={0}
        right={0}
        bottom={0}
        left={0}
        display="flex"
        alignItems="center"
        justifyContent="center"
        zIndex={2}
      >
        <Box
          color="integrator.700"
          fontSize="sm"
          fontWeight="medium"
          bg="integrator.100"
          px={3}
          py={2}
          rounded="md"
          role="dialog"
          aria-label={description}
        >
          {description}
        </Box>
      </Box>

      <VStack
        spacing={3}
        align="stretch"
        px={4}
        divider={<StackDivider />}
        userSelect="none"
        sx={{ filter: "blur(3px)" }}
      >
        <TransactionItem
          description="Payout to your bank account"
          subDescription="Payout"
          amount="USD 25.44"
          amountColor="successText"
        />
        <TransactionItem
          description="A purchase of something"
          subDescription="Purchase"
          amount="USD 12.11"
          amountColor="errorText"
        />
        <TransactionItem
          description="A sale of something else"
          subDescription="Sale"
          amount="USD 84.67"
          amountColor="successText"
        />
        <TransactionItem
          description="Payout to your bank account"
          subDescription="Payout"
          amount="USD 31.63"
          amountColor="successText"
        />
      </VStack>
    </Box>
  );
};

export const EmptyStateTransactions = ({ title, subtitle }) => {
  const {
    colors: { integrator },
  } = useTheme();
  return (
    <Box mt="40px" mb="100px" textAlign="center">
      <Flex mb="20px" justifyContent="center">
        <BidirectionalMoney color={integrator[100]} />
      </Flex>
      <Heading lineHeight="28px" mb="14px">
        {title}
      </Heading>
      <Box lineHeight="24px">{subtitle}</Box>
    </Box>
  );
};
